import React from 'react';
import { DIMENSIONS } from './smartMachine';

/**
 * Digiping SMART progress rail — five nodes with empty / partial / complete / active.
 * coachPendingDims: e.g. ['A','R'] while awaiting coach agreement.
 */
export function DigipingSmartRail({
  status = {},
  activeDimension = 'S',
  labels = {},
  coachPendingDims = [],
  coachCaption = 'Coach',
  onSelectDimension,
  hideCaption = false,
  className = ''
}) {
  const pendingSet = new Set(coachPendingDims || []);

  return (
    <div className={`digiping-smart-rail ${className}`.trim()} role="navigation" aria-label="SMART progress">
      <div className="digiping-smart-rail__track">
        {DIMENSIONS.map((dim, idx) => {
          const st = status[dim] || 'empty';
          const meta = labels[dim] || { letter: dim, label: dim };
          const isActive = activeDimension === dim;
          const coachPending = pendingSet.has(dim);
          const clickable = typeof onSelectDimension === 'function';

          return (
            <React.Fragment key={dim}>
              {idx > 0 && (
                <span
                  className={`digiping-smart-rail__link digiping-smart-rail__link--${status[DIMENSIONS[idx - 1]] || 'empty'}`}
                  aria-hidden="true"
                />
              )}
              <button
                type="button"
                className={[
                  'digiping-smart-rail__node',
                  `digiping-smart-rail__node--${dim.toLowerCase()}`,
                  `is-${st}`,
                  isActive ? 'is-active' : '',
                  coachPending ? 'is-coach-pending' : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
                disabled={!clickable}
                title={
                  coachPending
                    ? `${meta.letter} · ${meta.label} (${coachCaption})`
                    : `${meta.letter} · ${meta.label}`
                }
                aria-current={isActive ? 'step' : undefined}
                aria-label={`${meta.letter} ${meta.label}, ${st}${coachPending ? ', coach pending' : ''}`}
                onClick={() => clickable && onSelectDimension(dim)}
              >
                <span className="digiping-smart-rail__letter">{meta.letter}</span>
                {coachPending && (
                  <span className="digiping-smart-rail__coach" aria-hidden="true">
                    {coachCaption}
                  </span>
                )}
              </button>
            </React.Fragment>
          );
        })}
      </div>
      {!hideCaption && activeDimension && labels[activeDimension] && (
        <div className={`digiping-smart-rail__caption dp-em dp-em--${activeDimension.toLowerCase()}`}>
          {labels[activeDimension].letter} · {labels[activeDimension].label}
          {pendingSet.has(activeDimension) ? ` · ${coachCaption}` : ''}
        </div>
      )}
    </div>
  );
}

/**
 * Structured Digiping S→T snapshot for assessment.
 */
export function DigipingSmartSnapshot({
  rows = [],
  title,
  mindsetText,
  coachBanner,
  ctaLabel,
  onConfirm,
  DigipingMark,
  TypewriterText,
  RichText,
  submitting = false,
  errorText = null
}) {
  const [mindsetReady, setMindsetReady] = React.useState(!mindsetText);
  const [railReady, setRailReady] = React.useState(false);
  // Keep typed text stable across save retries so CTA errors don't replay the typewriter.
  const typedOnceRef = React.useRef(!mindsetText);

  React.useEffect(() => {
    const t = setTimeout(() => setRailReady(true), 200);
    return () => clearTimeout(t);
  }, []);

  React.useEffect(() => {
    if (!mindsetText) {
      typedOnceRef.current = true;
      setMindsetReady(true);
    }
  }, [mindsetText]);

  const showTypewriter = Boolean(TypewriterText && mindsetText && !typedOnceRef.current);

  return (
    <div className="assessment-tray assessment-tray--sticky-cta animate-fade-in">
      <div className="assessment-card digiping-snapshot-card assessment-card--body">
        <div className="assessment-card-head">
          {DigipingMark ? <DigipingMark size={36} /> : null}
          <h3>{title}</h3>
        </div>

        {coachBanner ? <p className="digiping-coach-banner">{coachBanner}</p> : null}

        <div className={`digiping-snapshot-grid ${railReady ? 'is-visible' : ''}`}>
          {rows.map((row) => (
            <div
              key={row.dimension}
              className={`digiping-snapshot-row digiping-snapshot-row--${row.dimension.toLowerCase()} is-${row.status}${
                row.coachBadge ? ' has-coach-badge' : ''
              }`}
            >
              <span className="digiping-snapshot-letter" aria-hidden="true">
                {row.letter}
              </span>
              <div className="digiping-snapshot-copy">
                <span className={`smart-label dp-em dp-em--${row.dimension.toLowerCase()}`}>
                  {row.label}
                  {row.coachBadge ? <span className="digiping-coach-pill">Coach</span> : null}
                </span>
                <span className="digiping-snapshot-summary">{row.summary}</span>
              </div>
            </div>
          ))}
        </div>

        {mindsetText ? (
          showTypewriter ? (
            <TypewriterText
              text={mindsetText}
              className="assessment-body typewriter-text"
              msPerChar={5}
              onComplete={() => {
                typedOnceRef.current = true;
                setMindsetReady(true);
              }}
            />
          ) : RichText ? (
            <RichText text={mindsetText} className="assessment-body" />
          ) : (
            <p className="assessment-body">{mindsetText}</p>
          )
        ) : null}

        {errorText ? <p className="inline-error-text">{errorText}</p> : null}
      </div>

      {(mindsetReady || typedOnceRef.current) && (
        <button
          onClick={onConfirm}
          className="assessment-btn ripple animate-slide-up"
          type="button"
          disabled={submitting}
          aria-busy={submitting}
        >
          {ctaLabel}
        </button>
      )}
    </div>
  );
}
